import Cancel from '@mui/icons-material/Cancel';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Delete from '@mui/icons-material/Delete';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import arrowIconRight from 'assets/icons/mui/icon-arrow-right.svg';
import arrowIconDown from 'assets/icons/mui/icons-arrow-down.svg';
import classNames from 'classnames';
import CategoryField from 'components/QuickenControls/CategoryField';
import QTip from 'components/QuickenControls/QTip';
import { ResourceRowStyles as styles } from 'components/ResourceRegister/styles';
import PropTypes from 'prop-types';
import React from 'react';
import { withStyles } from 'tss-react/mui';

import { chartOfAccountsTypes } from 'app-components/flux/chart-of-accounts';
import Dump from 'components/Dump';
import QCheckbox from 'components/QCheckbox';
import { CLIENTS_AND_PROJECTS_FILTER_RESOURCE_TYPE } from 'components/QFilterSelect/constants';
import { isBrowser } from 'utils/utils';

const fullWidth = { width: '100%' };

class ResourceRow extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      item: { ...props.item },
      isEditing: false,
      editingProp: null,
      selectOpen: false,
    };
  }

  componentDidMount() {
    this.trigerInitialEditing();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (JSON.stringify(nextProps.item) !== JSON.stringify(this.props.item)) {
      this.setState({
        item: { ...nextProps.item },
        isEditing: false,
        editingProp: null,
        selectOpen: false,
      });
    }

    if (
      this.props.editingProps.editingId === this.state.item.id &&
      nextProps.editingProps.editingId !== this.state.item.id &&
      nextProps.editingProps.cancelEditingId === this.state.item.id
    ) {
      this.onCancel(true);
    }
  }

  onBlur = (editProp) => (e) => {
    if (
      editProp !== 'parent' ||
      (editProp === 'parent' && !(e && e.relatedTarget) && !isBrowser('safari') && !isBrowser('firefox'))
    ) {
      this.changeEditingProp(null);
    }
  };

  onChange = (editProp) => (e) => {
    const getCategory = () => {
      if (this.props.getCategoryByID(e.id)) return this.props.getCategoryByID(e.id).name;
      return '';
    };
    const { item } = this.state;
    const newItem =
      editProp === 'parent'
        ? {
            ...item,
            parent: e && e.id && e.id !== '0' ? getCategory() : '',
            values: {
              ...item.values,
              parent: e,
            },
          }
        : {
            ...item,
            [editProp]: e.target.value,
          };

    this.setState({ item: newItem });
  };

  onCancel = (flagShowName = false) => {
    const {
      item: { name },
    } = this.state;

    if (this.hasChanges()) {
      this.props.setDialog(
        'Discard Changes',
        <>Are you sure you want to cancel changes to {flagShowName ? <strong>{name}</strong> : 'this'} category?</>,
        (dialog) => {
          if (['YES', 'Enter'].includes(dialog.action)) {
            this.resetChanges();
          }
          if (['CANCEL'].includes(dialog.action)) {
            this.cleanEditingData(true);
          }
        },
        ['CANCEL', 'YES'],
      );
      return;
    }
    this.resetChanges();
  };

  onCollapse = (e) => {
    e.stopPropagation();
    this.props.onCollapse(e);
  };

  onDelete = () => {
    this.props.onDelete(this.state.item);
  };

  onEditMode = (editingProp) => {
    this.requestEditing();
    this.changeEditingProp(editingProp);
  };

  onKeyPress = (nextRow) => (event) => {
    // If select is open all keyPress events must be handled by the selectComponent
    if (this.state.selectOpen) {
      event.stopPropagation();
    } else if (event.key === 'Enter') {
      this.onSave();
    } else if (event.key === 'Escape') {
      this.onCancel();
    } else if (event.key === 'Tab' && nextRow && nextRow.id && nextRow.editable) {
      setTimeout(() => this.onEditMode(nextRow.id), 0);
    }
  };

  onSave = () => {
    if (this.hasChanges()) {
      this.props.onSave(this.state.item);
      this.cleanEditingData();
    } else {
      this.onCancel();
    }
  };

  onSelectToggle = () => {
    this.setState({ selectOpen: !this.state.selectOpen });
  };

  changeEditingProp(editingProp) {
    const isEditing = Boolean(editingProp);

    if (!isEditing && !this.hasChanges()) {
      this.cleanEditingData();
    }

    this.setState(() => ({ isEditing, editingProp }));
  }

  getArgs() {
    const { classes, dataDictionary, isChild, isExpanded, isParent, editingProps, rowIndex, onSelect, selectedItems } =
      this.props;
    const { item, isEditing, editingProp, selectOpen } = this.state;
    const { isNewCategory = false } = item;
    const showEditing = isEditing || this.hasChanges() || isNewCategory;
    const index = rowIndex.length < 2 ? [...rowIndex, 0] : rowIndex;
    const baseId = `category-${index.join('-')}`;

    return {
      classes,
      dataDictionary,
      isChild,
      isExpanded,
      isParent,
      editingProps,
      item,
      isEditing,
      editingProp,
      showEditing,
      isNewCategory,
      baseId,
      selectOpen,
      onSelect,
      selectedItems,
    };
  }

  hasChanges() {
    return JSON.stringify(this.props.item) !== JSON.stringify(this.state.item);
  }

  resetChanges() {
    const {
      item: { isNewCategory = false },
    } = this.state;

    this.setState({
      item: { ...this.props.item },
      isEditing: false,
      editingProp: null,
      selectOpen: false,
    });

    if (isNewCategory) {
      this.props.onRemoveNewItem();
    }

    this.cleanEditingData();
  }

  requestEditing() {
    this.props.editingProps.requestEditing(this.state.item.id);
  }

  cleanEditingData(flagSendId = false) {
    this.props.editingProps.cleanEditingData(flagSendId ? this.state.item.id : null);
  }

  trigerInitialEditing() {
    const {
      editingProp,
      item: { isNewCategory = false },
    } = this.state;

    if (isNewCategory && !this.hasChanges() && editingProp === null) {
      this.onEditMode('name');
    }
  }

  /* eslint-disable jsx-a11y/no-noninteractive-tabindex */
  renderCollapseIcon(args) {
    const { narrowMode } = this.props;
    const { classes, baseId, isExpanded, isParent, item } = args;
    const { resourceType } = this.props;
    let hideIcon = false;
    if (resourceType === CLIENTS_AND_PROJECTS_FILTER_RESOURCE_TYPE && isParent && item?.children?.length === 0) {
      // For Client and Projects filter, if parent(client) node doesn't have any child nodes, then do not display any arrow icon for parent node
      hideIcon = true;
    }
    return isParent ? (
      <ButtonBase
        tabIndex={-1}
        style={{ visibility: hideIcon ? 'hidden' : 'visible' }}
        id={`${baseId}-collapse-button`}
        className={classNames(classes.collapseIcon, narrowMode ? 'narrowMode' : '')}
        onClick={this.onCollapse}
      >
        <img
          tabIndex={0}
          alt={isExpanded ? 'Expand List' : 'Collapse List'}
          src={isExpanded ? arrowIconDown : arrowIconRight}
        />
      </ButtonBase>
    ) : (
      <div className={classNames(classes.collapseIcon, narrowMode ? 'narrowMode' : '')} />
    );
  }

  getIsIndeterminate(item, selectedItems) {
    let numSelectedChildren = 0;
    let numTotalChildren = 0;

    function countSelected(node) {
      if (!node?.children || node.children.length === 0) return;

      for (const child of node.children) {
        numTotalChildren += 1;

        if (child?.id && selectedItems.includes(child.id)) {
          numSelectedChildren += 1;
        }

        // Recursively check deeper children
        countSelected(child);
      }
    }

    countSelected(item);

    const allSelected = numSelectedChildren === numTotalChildren && numTotalChildren > 0;
    const noneSelected = numSelectedChildren === 0;
    return !allSelected && !noneSelected && numTotalChildren > 0;
  }

  renderActionIcons(args) {
    const { classes, baseId, showEditing, isNewCategory, row, onSelect, item, selectedItems } = args;
    const indeterminate = this.getIsIndeterminate(item, selectedItems);

    if (row.type === 'actionsSelect') {
      const selected = selectedItems?.includes(item.id);
      return (
        <div key={`${row.id}-${baseId}-action`} className={classes.actionsSelectContainer}>
          <QCheckbox
            tabIndex={-1}
            disabled={this.props.disabled}
            indeterminate={indeterminate}
            onChange={!this.props.disabled ? () => onSelect(item) : null}
            checked={selected}
            id={`${baseId}-select-box`}
            aria-label='Select'
            classes={{ root: classes.checkboxScale }}
          />
        </div>
      );
    }

    return (
      <div key={`${row.id}-${baseId}-action`} className={classes.actionsIconsContainer} style={{ flex: row.flex }}>
        {!isNewCategory && (
          <IconButton
            id={`${baseId}-delete-button`}
            className={classNames(classes.hoverIcon)}
            aria-label='Delete'
            onClick={this.onDelete}
            size='large'
          >
            <Delete className={classes.deleteIcon} />
          </IconButton>
        )}
        {showEditing && (
          <div className={classes.editButtonsContainer}>
            <IconButton
              id={`${baseId}-cancel-button`}
              className={classes.actionIcon}
              aria-label='Cancel'
              onClick={() => this.onCancel()}
              size='large'
            >
              <Cancel className={classes.cancelIcon} />
            </IconButton>
            <IconButton
              id={`${baseId}-save-button`}
              className={classes.actionIcon}
              aria-label='Save'
              onClick={this.onSave}
              size='large'
            >
              <CheckCircle className={classes.saveIcon} />
            </IconButton>
          </div>
        )}
      </div>
    );
  }

  renderItemContent(args) {
    const { classes, narrowMode } = this.props;
    const { dataDictionary, isParent, isChild, isEditing, editingProp, item } = args;

    return (
      <div className={classNames(classes.itemContentContainer, narrowMode ? 'narrowMode' : '')}>
        <Dump obj={item} />
        {dataDictionary.map((row, index) => {
          const showIndent = isChild && !isParent;
          let root;
          if (showIndent) {
            root = classes?.[`itemIdent-L${item.level}`]; // ACME just have L2 || Quicken has L2 to L16
          } else {
            root = classes.firstItem; // L1
          }

          const nextRow = index + 1 < dataDictionary.size ? dataDictionary.get(index + 1) : undefined;

          if (row.type === 'actions' || row.type === 'actionsSelect') {
            return this.renderActionIcons({ ...args, row, index });
          }

          if (isEditing && editingProp === row.id && row.editable) {
            return this.renderFields({ ...args, row, index, root, nextRow });
          }

          return this.renderTextLabel({ ...args, row, index, primary: root });
        })}
      </div>
    );
  }

  renderTextLabel(args) {
    const { classes, row, item, isChild, baseId, isParent } = args;
    let canGotoEditMode = true;
    let listClassname = classNames(classes.listItemText, row.editable ? classes.labelHover : '');
    let text = item[row.id] ? item[row.id] : row.defaultValue || row.placeholder;

    if (row.type === 'select') {
      text = (row.choices.find((x) => x.value === item[row.id]) || {}).title;
      canGotoEditMode = !isChild;
      listClassname = classNames(listClassname, row.editable && isChild ? classes.labelNoHover : '', {
        [classes.noCursorPointer]: !canGotoEditMode,
      });
    }
    return row.type === 'CategoryField' && isParent && row.editable ? (
      <div key={`${row.id}-${baseId}-text`} className={classes.uneditableCat} style={{ flex: row.flex }}>
        <QTip arrow placement='right' title='Subcategories can only be assigned to a single parent category'>
          <Typography id={`${baseId}-${row.id}-text`} variant='bodyRegular'>
            {text}
          </Typography>
        </QTip>
      </div>
    ) : (
      <div key={`${row.id}-${baseId}-text`} style={{ flex: row.flex }} className={listClassname}>
        <QTip placement='right' title={text} wrapOnly>
          <Typography
            className={classes.listItemTextParagraph}
            id={`${baseId}-${row.id}-text`}
            onClick={row.editable ? () => canGotoEditMode && this.onEditMode(row.id) : null}
            onFocus={row.editable ? () => canGotoEditMode && this.onEditMode(row.id) : null}
            variant='paragraphRegular'
            tabIndex={0}
          >
            {text}
          </Typography>
        </QTip>
      </div>
    );
  }

  renderFields(args) {
    const { classes, row, item, baseId, selectOpen, nextRow, isParent } = args;
    const sharedProps = {
      id: `${baseId}-${row.id}-field`,
      key: `${row.id}-${baseId}`,
      autoFocus: true,
      onBlur: this.onBlur(row.id),
      onChange: this.onChange(row.id),
      onKeyPress: this.onKeyPress(nextRow),
      onKeyDown: this.onKeyPress(nextRow),
      placeholder: row.placeholder,
      value: item[row.id] ? item[row.id] : '',
    };
    let fieldProps = {};
    switch (row.type) {
      case 'string': {
        return (
          <div key={sharedProps.key} className={classNames(classes.listItemText, classes.catName)}>
            <InputBase
              className={classes.textField}
              value={item.name}
              style={{ width: '100%' }}
              {...sharedProps}
              inputProps={{ style: { paddingTop: 4, paddingBottom: 4 } }}
            />
          </div>
        );
      }
      case 'select': {
        fieldProps = {
          select: true,
          classes: {
            root: classes.typeTextField,
          },
          variant: 'standard',
          SelectProps: {
            disableUnderline: true,
            open: selectOpen,
            onOpen: this.onSelectToggle,
            onClose: this.onSelectToggle,
            classes: { select: classes.selectField },
          },
          children: this.renderSelectItems(row.choices),
        };
        return (
          <div key={sharedProps.key} className={classNames(classes.listItemText, classes.catType)}>
            <TextField {...sharedProps} {...fieldProps} />
          </div>
        );
      }
      case 'CategoryField': {
        return isParent ? (
          <div key={sharedProps.key} style={{ flex: row.flex }} className={classes.listItemTextSubCatParent}>
            -
          </div>
        ) : (
          // ^ is needed so tabbing into the element cant override not being allowed to select a subcategory
          <div key={sharedProps.key} style={{ flex: row.flex }} className={classes.listItemTextSubCat}>
            <CategoryField
              editable
              plain
              {...sharedProps}
              blurOnMenuClose
              disableUnderline
              allowNone
              depth={1}
              InputProps={{ classes: { root: classes.widthHundred } }}
              filterFn={(cat) =>
                cat.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY ||
                cat.type === chartOfAccountsTypes.CoaTypeEnum.NONE
              }
              menuPosition='absolute'
              value={item.values.parent || ''}
              style={fullWidth}
            />
          </div>
        );
      }
      default:
        return null;
    }
  }

  renderSelectItems(choices) {
    return choices.map((option) => (
      <MenuItem key={option.value} value={option.value} style={{ justifyContent: 'center' }}>
        {option.title}
      </MenuItem>
    ));
  }

  render() {
    const args = this.getArgs();
    const { classes, showEditing, baseId } = args;
    const itemClasses = classNames(classes.itemContainer, { [classes.itemContainerIsEditing]: showEditing });
    const { onSelect } = this.props;

    return (
      <List id={`${baseId}-row-container`} dense className={classes.listContainer}>
        <ListItem
          tabIndex={0}
          id={`${baseId}-row-item`} //  THE WORDS row-item ARE TIED TO A JQUERY SELECTOR THAT ADDS ARROW KEY FUNCTIONALITY TO SELECTING LIST ITEMS
          className={itemClasses}
          onClick={onSelect && !this.props.disabled ? () => onSelect(args.item) : null}
          onKeyDown={
            onSelect && !this.props.disabled
              ? (e) => {
                  if (e.key === 'Enter' && e.target.localName !== 'img') {
                    onSelect(args.item);
                    e.stopPropagation();
                    e.preventDefault();
                  }
                }
              : null
          }
        >
          {this.renderCollapseIcon(args)}
          {this.renderItemContent(args)}
        </ListItem>
      </List>
    );
  }
}

ResourceRow.defaultProps = {
  isChild: false,
};

ResourceRow.propTypes = {
  classes: PropTypes.object,
  item: PropTypes.object,
  narrowMode: PropTypes.bool,
  rowIndex: PropTypes.array,
  isChild: PropTypes.bool,
  isExpanded: PropTypes.bool,
  isParent: PropTypes.bool,
  dataDictionary: PropTypes.object,
  onCollapse: PropTypes.func,
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
  onSelect: PropTypes.func,
  selectedItems: PropTypes.object,
  onRemoveNewItem: PropTypes.func,
  getCategoryByID: PropTypes.func,
  setDialog: PropTypes.func,
  editingProps: PropTypes.object,
  disabled: PropTypes.bool,
  resourceType: PropTypes.string,
};

export default withStyles(ResourceRow, styles);
